import { Divider, Stack, Typography } from '@mui/material';
import {
  BackupPolicyType,
  ScheduleType,
  type BackupSchedule,
} from '@repo/api-gw-sdk';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';

import type { StepProps } from '@/components/wizard/StepProps';

import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { RESTRICTED_POLICY_TIMES_PER_DAY } from './BackupPolicySettingsStandard';
import { BackupPolicyScheduleList } from './backupPolicyScheduleList';
import { BackupPolicySettingsStepContainer } from './backupPolicySettingsStepContainer';
import { BackupPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';
import {
  getDefaultHighFrequencySchedules,
  getDefaultStandardSchedules,
} from './defaultBackupPolicy';
import { ScheduleModeSelector } from './scheduleModeSelector';

import { dailyFrequency, getFrequencyByWindow } from '../frequency';

export const BackupPolicySettingsPitr = (
  props: StepProps<BackupPolicyEditState>
) => {
  const [useUtc, setUseUtc] = useState(
    !!props.wizardState.schedules?.[0]?.useUtc
  );

  const addSchedule = (scheduleType: ScheduleType) => {
    let vaultId = '';
    const newState = {
      ...props.wizardState,
    };

    if (scheduleType === ScheduleType.SCHEDULE_TYPE_PITR) {
      const standardSchedules = props.wizardState.schedules?.filter(
        (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_STANDARD
      );
      vaultId =
        standardSchedules?.find((standardSchedule) => {
          if (!standardSchedule.vaultId) {
            return false;
          }
          const frequency = getFrequencyByWindow(standardSchedule.windows);
          return (
            frequency?.timesPerDay ||
            frequency?.windows === dailyFrequency.windows
          );
        })?.vaultId || '';
    }

    newState.schedules = [
      ...(newState.schedules ?? []),
      {
        ...(props.wizardState.policyType ===
          BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY ||
        scheduleType === ScheduleType.SCHEDULE_TYPE_PITR
          ? getDefaultHighFrequencySchedules(vaultId, scheduleType)[0]
          : getDefaultStandardSchedules(vaultId, scheduleType)[0]),
      },
    ];

    props.setWizardState(newState);
  };

  const updateSchedule = (schedule: BackupSchedule) => {
    const schedules =
      props.wizardState.schedules?.map((s) =>
        s.id === schedule.id ? schedule : s
      ) ?? [];

    props.setWizardState({ ...props.wizardState, schedules });
  };

  const removeSchedule = (schedule: BackupSchedule) => {
    if (props.wizardState.schedules?.length === 1) return;
    props.setWizardState({
      ...props.wizardState,
      schedules: props.wizardState.schedules?.filter(
        (s) => s.id !== schedule.id
      ),
    });
  };

  const duplicateSchedule = (schedule: BackupSchedule) => {
    props.setWizardState({
      ...props.wizardState,
      schedules: [
        ...(props.wizardState.schedules ?? []),
        { ...schedule, id: uuid() },
      ],
    });
  };

  const timesPerDay = RESTRICTED_POLICY_TIMES_PER_DAY;

  const canGoNext = () => {
    if (!props.wizardState.schedules?.length) {
      return false;
    }

    for (const schedule of props.wizardState.schedules) {
      if (!schedule.vaultId || !schedule.backupRetention) {
        return false;
      }
    }

    if (
      props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR
    ) {
      const pitrSchedules = props.wizardState.schedules.filter(
        (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_PITR
      );

      // Each vault can only be used once in PiTR
      if (
        new Set(pitrSchedules.map((x) => x.vaultId)).size !==
        pitrSchedules.length
      ) {
        return false;
      }

      const standardSchedules = props.wizardState.schedules.filter(
        (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_STANDARD
      );

      const allPitrSchedulesHaveRelatedStandard = pitrSchedules.every(
        (pitrSchedule) => {
          return standardSchedules.some((standardSchedule) => {
            const frequency = getFrequencyByWindow(standardSchedule.windows);
            return (
              standardSchedule.vaultId === pitrSchedule.vaultId &&
              standardSchedule.backupRetention >=
                pitrSchedule.backupRetention &&
              (frequency?.timesPerDay ||
                frequency?.windows === dailyFrequency.windows)
            );
          });
        }
      );

      return allPitrSchedulesHaveRelatedStandard;
    }

    return true;
  };

  return (
    <BackupPolicySettingsStepContainer {...props} canGoNext={canGoNext}>
      <BackupPolicySupportedDisclaimer
        policyType={props.wizardState.policyType}
      />
      <Stack className='px-[min(100px,5vw)] pt-[min(60px,3vw)]'>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: '16px' }}
        >
          <Typography variant='body1'>Backup schedules</Typography>
          <ScheduleModeSelector
            wizardState={props.wizardState}
            setWizardState={props.setWizardState}
            useUtc={useUtc}
            setUseUtc={setUseUtc}
            timesPerDay={timesPerDay}
          />
        </Stack>
        <Stack divider={<Divider />} gap='40px'>
          <BackupPolicyScheduleList
            policyType={props.wizardState.policyType}
            schedules={
              props.wizardState.schedules?.filter(
                (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_STANDARD
              ) ?? []
            }
            deleteAllowed={
              (props.wizardState.schedules?.filter(
                (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_STANDARD
              ).length ?? 0) > 1
            }
            onUpdate={updateSchedule}
            onDuplicate={duplicateSchedule}
            onDelete={removeSchedule}
            mode={props.wizardState.mode}
            onAdd={() => addSchedule(ScheduleType.SCHEDULE_TYPE_STANDARD)}
            addButtonText='Add Backup Schedule'
          />
          {props.wizardState.policyType ===
            BackupPolicyType.BACKUP_POLICY_TYPE_PITR && (
            <BackupPolicyScheduleList
              policyType={props.wizardState.policyType}
              title='Point-in-time recovery (PiTR) schedules'
              subtitle='Requires a daily or more frequent backup schedule above, that uses the same vault and has a longer retention period. Each vault can only be used once in this section.'
              deleteAllowed={true}
              schedules={
                props.wizardState.schedules?.filter(
                  (x) => x.scheduleType === ScheduleType.SCHEDULE_TYPE_PITR
                ) ?? []
              }
              onUpdate={updateSchedule}
              onDuplicate={duplicateSchedule}
              onDelete={removeSchedule}
              mode={props.wizardState.mode}
              onAdd={() => addSchedule(ScheduleType.SCHEDULE_TYPE_PITR)}
              addButtonText='Add PiTR Schedule'
            />
          )}
        </Stack>
      </Stack>
    </BackupPolicySettingsStepContainer>
  );
};
